import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";

interface ContactInfo {
  name: string;
  company: string;
  email: string;
  phone: string;
  website: string;
}

type AnswerId =
  | "businessType"
  | "acquisition"
  | "blocker"
  | "goal"
  | "annualRevenue"
  | "whyNow"
  | "readyToInvest";

type Answers = Record<AnswerId, string>;

const emptyContact: ContactInfo = { name: "", company: "", email: "", phone: "", website: "" };

const emptyAnswers: Answers = {
  businessType: "",
  acquisition: "",
  blocker: "",
  goal: "",
  annualRevenue: "",
  whyNow: "",
  readyToInvest: "",
};

const questions: {
  id: AnswerId;
  question: string;
  hint?: string;
  type?: "textarea";
  options?: { value: string; label: string }[];
}[] = [
  {
    id: "businessType",
    question: "Quel est votre type d'entreprise?",
    options: [
      { value: "Services B2B", label: "Services B2B" },
      { value: "Coaching / Consultation", label: "Coaching / Consultation" },
      { value: "PME locale", label: "PME locale" },
      { value: "Commerce / E-commerce", label: "Commerce / E-commerce" },
      { value: "Autre", label: "Autre" },
    ],
  },
  {
    id: "acquisition",
    question: "Comment générez-vous vos clients présentement?",
    options: [
      { value: "Contenu organique", label: "Contenu organique" },
      { value: "Publicité payante", label: "Publicité payante" },
      { value: "Références", label: "Références" },
      { value: "Rien de structuré", label: "Rien de structuré" },
    ],
  },
  {
    id: "blocker",
    question: "Quel est votre principal frein à la croissance?",
    options: [
      { value: "Manque de leads qualifiés", label: "Manque de leads qualifiés" },
      { value: "Leads non qualifiés", label: "Leads non qualifiés" },
      { value: "Publicité qui ne convertit pas", label: "Publicité qui ne convertit pas" },
      { value: "Contenu sans résultats", label: "Contenu sans résultats" },
      { value: "Aucun système de suivi", label: "Aucun système de suivi" },
    ],
  },
  {
    id: "goal",
    question: "Quel est votre objectif pour les 12 prochains mois?",
    options: [
      { value: "Plus de leads qualifiés", label: "Plus de leads qualifiés" },
      { value: "Plus de ventes", label: "Plus de ventes" },
      { value: "Système d'acquisition stable", label: "Système d'acquisition stable" },
      { value: "Doubler le chiffre d'affaires", label: "Doubler le chiffre d'affaires" },
    ],
  },
  {
    id: "annualRevenue",
    question: "Quel est votre chiffre d'affaires annuel?",
    options: [
      { value: "Moins de 250 000 $", label: "Moins de 250 000 $" },
      { value: "250 000 $ – 1 M$", label: "250 000 $ – 1 M$" },
      { value: "1 M$ – 5 M$", label: "1 M$ – 5 M$" },
      { value: "5 M$ et plus", label: "5 M$ et plus" },
    ],
  },
  {
    id: "whyNow",
    question: "Pourquoi maintenant?",
    hint: "Ce qui vous pousse à bâtir un vrai système de croissance aujourd'hui.",
    type: "textarea",
  },
  {
    id: "readyToInvest",
    question: "Êtes-vous prêt à investir dans un système de croissance?",
    options: [
      { value: "Oui, prêt à investir", label: "Oui, prêt à investir" },
      { value: "Oui, selon le plan proposé", label: "Oui, selon le plan proposé" },
      { value: "À valider avec mon équipe", label: "À valider avec mon équipe" },
      { value: "Pas pour le moment", label: "Pas pour le moment" },
    ],
  },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AccompagnementFormModal = ({ open, onOpenChange }: Props) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"contact" | "questions" | "merci">("contact");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [contact, setContact] = useState<ContactInfo>(emptyContact);
  const [answers, setAnswers] = useState<Answers>(emptyAnswers);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone: string) => phone.replace(/\D/g, "").length >= 10;

  const errors = {
    name: touched.name && !contact.name.trim() ? "Requis" : "",
    company: touched.company && !contact.company.trim() ? "Requis" : "",
    email:
      touched.email && (!contact.email.trim() ? "Requis" : !isValidEmail(contact.email) ? "Courriel invalide" : ""),
    phone: touched.phone && (!contact.phone.trim() ? "Requis" : !isValidPhone(contact.phone) ? "Numéro invalide" : ""),
  };

  const isContactValid =
    contact.name.trim() && contact.company.trim() && isValidEmail(contact.email) && isValidPhone(contact.phone);

  const totalSteps = questions.length + 1;
  const currentStepIndex = phase === "contact" ? 0 : currentQuestion + 1;
  const progress = phase === "merci" ? 100 : Math.round(((currentStepIndex + 1) / totalSteps) * 100);

  const contactPayload = () => ({
    name: contact.name,
    email: contact.email,
    phone: contact.phone,
    company: contact.company,
    website: contact.website,
  });

  /** Enregistre les coordonnées dès l'étape 1, en arrière-plan (jamais bloquant). */
  const saveContactStage = () => {
    if (requestId) return;
    supabase.functions
      .invoke("accompagnement-request", {
        body: { stage: "contact", ...contactPayload() },
      })
      .then(({ data, error }) => {
        if (error) {
          console.error("Error saving contact stage:", error);
          return;
        }
        const id = (data as { id?: string } | null)?.id;
        if (id) setRequestId(id);
      })
      .catch((err) => console.error("Error saving contact stage:", err));
  };

  const submit = async (finalAnswers: Answers) => {
    if (submitting) return;
    setSubmitting(true);

    const submissionData = {
      stage: "complete" as const,
      ...(requestId ? { request_id: requestId } : {}),
      ...contactPayload(),
      business_type: finalAnswers.businessType,
      client_generation: finalAnswers.acquisition,
      main_challenge: finalAnswers.blocker,
      goal: finalAnswers.goal,
      annual_revenue: finalAnswers.annualRevenue,
      why_now: finalAnswers.whyNow,
      ready_to_invest: finalAnswers.readyToInvest,
    };

    try {
      const { error } = await supabase.functions.invoke("accompagnement-request", {
        body: submissionData,
      });
      if (error) throw error;

      setSubmitting(false);
      navigate("/merci/accompagnement");
    } catch (err) {
      console.error("Error saving submission:", err);
      setSubmitting(false);
      setPhase("merci");
    }
  };




  const handleAnswer = (value: string) => {
    const id = questions[currentQuestion].id;
    const updated = { ...answers, [id]: value };
    setAnswers(updated);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion((prev) => prev + 1), 240);
    } else {
      submit(updated);
    }
  };

  const goBack = () => {
    if (phase === "questions") {
      if (currentQuestion > 0) setCurrentQuestion((prev) => prev - 1);
      else setPhase("contact");
    }
  };

  const fieldClass = (hasError: string) =>
    `h-12 rounded-none border-0 border-b bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/50 transition-colors focus-visible:border-primary focus-visible:ring-0 ${
      hasError ? "border-destructive/60" : "border-border"
    }`;

  const contactField = (id: keyof ContactInfo, label: string, error: string, type: string = "text") => (
    <div>
      <Label
        htmlFor={`acc-${id}`}
        className="flex items-baseline justify-between text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
      >
        <span>{label}</span>
        {error && <span className="normal-case tracking-normal text-destructive">{error}</span>}
      </Label>
      <Input
        id={`acc-${id}`}
        type={type}
        value={contact[id]}
        onChange={(e) => setContact((p) => ({ ...p, [id]: e.target.value }))}
        onBlur={() => setTouched((p) => ({ ...p, [id]: true }))}
        className={fieldClass(error)}
      />
    </div>
  );

  const q = questions[currentQuestion];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] w-[calc(100vw-24px)] max-w-xl overflow-y-auto border-border bg-background p-0 sm:w-full">
        <DialogTitle className="sr-only">Présenter mon entreprise</DialogTitle>
        <DialogDescription className="sr-only">
          Formulaire de présentation d'entreprise pour l'accompagnement Agence 8020.
        </DialogDescription>

        {/* Barre de progression */}
        {phase !== "merci" && (
          <div className="sticky top-0 z-10 bg-background/95 px-6 pt-6 backdrop-blur md:px-8">
            <div className="mb-3 flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70">
              <span>
                {phase === "contact" ? "Coordonnées" : `Question ${currentQuestion + 1} / ${questions.length}`}
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-px w-full bg-border">
              <motion.div
                className="h-px bg-primary"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        )}

        <div className="px-6 pb-8 pt-8 md:px-8">
          <AnimatePresence mode="wait">
            {phase === "contact" && (
              <motion.div
                key="contact"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <h2 className="font-heading text-[clamp(1.5rem,4.4vw,2.1rem)] font-semibold leading-[1.1] tracking-tight">
                  Parlez-nous de
                  <span className="block text-muted-foreground">votre entreprise.</span>
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Quelques informations, puis 7 questions rapides. Moins de deux minutes.
                </p>

                <div className="mt-9 grid gap-7 sm:grid-cols-2">
                  {contactField("name", "Nom", errors.name)}
                  {contactField("company", "Entreprise", errors.company)}
                  {contactField("email", "Courriel professionnel", errors.email, "email")}
                  {contactField("phone", "Téléphone", errors.phone, "tel")}
                  <div className="sm:col-span-2">{contactField("website", "Site web (optionnel)", "")}</div>
                </div>

                <Button
                  onClick={() => {
                    setTouched({ name: true, company: true, email: true, phone: true });
                    if (isContactValid) {
                      saveContactStage();
                      setPhase("questions");
                    }
                  }}
                  className="group mt-10 h-auto w-full rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  Continuer
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </motion.div>
            )}

            {phase === "questions" && q && (
              <motion.div
                key={`q-${currentQuestion}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <h2 className="font-heading text-[clamp(1.35rem,4vw,1.9rem)] font-semibold leading-[1.14] tracking-tight">
                  {q.question}
                </h2>
                {q.hint && <p className="mt-3 text-sm text-muted-foreground">{q.hint}</p>}

                {q.type === "textarea" ? (
                  <div className="mt-8">
                    <Textarea
                      value={answers.whyNow}
                      onChange={(e) => setAnswers((p) => ({ ...p, whyNow: e.target.value }))}
                      placeholder="Votre réponse..."
                      className="min-h-[120px] resize-none rounded-none border-0 border-b border-border bg-transparent px-0 text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-0"
                    />
                    <Button
                      onClick={() => handleAnswer(answers.whyNow)}
                      disabled={!answers.whyNow.trim() || submitting}
                      className="group mt-8 h-auto w-full rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                      Continuer
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-8">
                    {q.options?.map((option) => {
                      const selected = answers[q.id] === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() => handleAnswer(option.value)}
                          disabled={submitting}
                          className={`group flex w-full items-center justify-between border-t border-border/70 py-5 text-left transition-colors last:border-b ${
                            selected ? "text-primary" : "text-foreground hover:text-primary"
                          }`}
                        >
                          <span className="text-base md:text-lg">{option.label}</span>
                          {selected ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <ArrowRight className="h-4 w-4 -translate-x-2 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={goBack}
                  className="mt-8 block text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  ← Retour
                </button>

                {submitting && <p className="mt-6 text-sm text-muted-foreground">Envoi en cours...</p>}
              </motion.div>
            )}

            {phase === "merci" && (
              <motion.div
                key="merci"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="py-4"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-primary">Demande reçue</p>
                <h2 className="mt-6 font-heading text-[clamp(1.6rem,4.6vw,2.4rem)] font-semibold leading-[1.1] tracking-tight">
                  Merci.
                </h2>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                  Notre équipe prend connaissance de votre demande et communique avec vous par téléphone si nous croyons
                  pouvoir avoir un réel impact sur votre croissance.
                </p>
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  className="mt-10 h-auto rounded-full border border-border px-7 py-3 text-base font-medium text-foreground hover:bg-secondary"
                >
                  Fermer
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccompagnementFormModal;
